import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Heart, HelpCircle, Minus, Plus, Truck, X } from "lucide-react";
import { API, useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [length, setLength] = useState(null);
  const [colour, setColour] = useState("");
  const [qty, setQty] = useState(1);
  const [isWish, setIsWish] = useState(false);
  const [chartOpen, setChartOpen] = useState(false);
  const { user } = useAuth();
  const { add } = useCart();

  useEffect(() => {
    axios.get(`${API}/shop/products/${slug}`).then((r) => {
      const data = r.data;
      // For NøoksProRange bundles, derive lengths from length_prices
      if (data.bundle && data.length_prices) {
        data.lengths = Object.keys(data.length_prices).map((k) => `${k}"`);
      }
      setP(data);
      if (data.lengths?.length) setLength(data.lengths[0]);
      // Do NOT preselect a colour — force customer to choose.
      // Bundle colour is not preselected — must be an explicit choice
    });
    if (user) {
      axios
        .get(`${API}/me/wishlist`)
        .then((r) => setIsWish(r.data.includes(slug)))
        .catch(() => {});
    }
  }, [slug, user]);

  const bundlePrice = () => {
    if (!p?.bundle || !length) return null;
    const key = length.replace('"', "");
    return p.length_prices[key];
  };
  const effectivePrice = () => bundlePrice() ?? p?.price ?? 0;

  // Group bundle colours by family for the dropdown
  const groupedBundleColours = useMemo(() => {
    if (!p?.bundle_colours) return null;
    const groups = {};
    for (const c of p.bundle_colours) {
      (groups[c.family] ||= []).push(c);
    }
    return groups;
  }, [p]);

  const selectedBundleColour = useMemo(() => {
    if (!p?.bundle_colours || !colour) return null;
    return p.bundle_colours.find((c) => c.code === colour) || null;
  }, [p, colour]);

  const toggleWish = async () => {
    if (!user) {
      toast("Sign in to save to wishlist");
      return;
    }
    if (isWish) {
      setIsWish(false);
      await axios.delete(`${API}/me/wishlist/${slug}`);
    } else {
      setIsWish(true);
      await axios.post(`${API}/me/wishlist`, { slug });
    }
  };

  const addToCart = () => {
    if (p.lengths?.length && !length) return toast.error("Choose a length");
    if (p.bundle_colours?.length && !colour) return toast.error("Choose a colour");
    if (p.colours?.length && !colour) return toast.error("Choose a colour");
    const cartProduct = { ...p, price: effectivePrice() };
    // Persist a friendly colour label for cart/order display: "#8 · Light Chestnut"
    const displayColour = selectedBundleColour
      ? `${selectedBundleColour.code} · ${selectedBundleColour.label}`
      : colour || null;
    add(cartProduct, {
      length,
      colour: p.bundle_colours?.length ? selectedBundleColour?.code : colour,
      colourLabel: displayColour,
      quantity: qty,
    });
    toast.success("Added to cart");
  };

  if (!p) {
    return (
      <div className="pt-40 pb-24 text-center text-[hsl(var(--onyx)/0.5)]">
        Loading…
      </div>
    );
  }

  return (
    <div data-testid={`page-product-${slug}`} className="pt-32 pb-24">
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
        <Link
          to="/shop"
          className="text-sm text-[hsl(var(--onyx)/0.6)] hover:text-[hsl(var(--onyx))]"
        >
          ← Back to shop
        </Link>

        <div className="grid gap-12 lg:grid-cols-12 mt-6">
          <div className="lg:col-span-7">
            <div className="rounded-[2.25rem] overflow-hidden hover-zoom aspect-square bg-blush">
              <img
                src={p.image}
                alt={p.name}
                className="w-full h-full object-cover"
              />
            </div>
            {p.images?.length > 1 && (
              <div className="mt-4 grid grid-cols-4 gap-3">
                {p.images.slice(0, 4).map((img, i) => (
                  <div
                    key={i}
                    className="aspect-square rounded-2xl overflow-hidden bg-blush"
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="overline">{p.category}</div>
            <h1 className="font-display text-4xl sm:text-5xl mt-3 tracking-tight text-balance">
              {p.name}
            </h1>
            <div
              className="mt-4 font-display text-4xl text-[hsl(var(--onyx))]"
              data-testid="product-price"
            >
              £{effectivePrice().toFixed(2)}
              {p.bundle && p.unit && (
                <span className="ml-3 text-sm text-[hsl(var(--onyx)/0.5)] uppercase tracking-widest">
                  {p.unit}
                </span>
              )}
            </div>
            <p className="mt-4 text-[hsl(var(--onyx)/0.7)] leading-relaxed whitespace-pre-line">
              {p.longDesc}
            </p>

            {p.lengths?.length ? (
              <div className="mt-8">
                <div className="overline mb-3">Length</div>
                <div className="flex flex-wrap gap-2">
                  {p.lengths.map((l) => (
                    <button
                      key={l}
                      onClick={() => setLength(l)}
                      data-testid={`length-${l.replace(/\W/g, "")}`}
                      className={`px-4 py-2 rounded-full text-sm transition-all duration-300 ${
                        length === l
                          ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]"
                          : "border border-[hsl(var(--onyx)/0.15)] hover:border-[hsl(var(--onyx))]"
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {p.bundle_colours?.length ? (
              <div className="mt-8">
                <div className="flex items-center justify-between mb-3">
                  <div className="overline">Colour</div>
                  {p.colour_chart && (
                    <button
                      type="button"
                      onClick={() => setChartOpen(true)}
                      data-testid="open-colour-chart"
                      className="text-xs uppercase tracking-widest text-[hsl(var(--burgundy))] hover:underline"
                    >
                      View colour chart
                    </button>
                  )}
                </div>
                <select
                  value={colour}
                  onChange={(e) => setColour(e.target.value)}
                  data-testid="colour-select"
                  className="w-full h-12 rounded-2xl border border-[hsl(var(--onyx)/0.15)] bg-white px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                >
                  <option value="">-- Choose a colour --</option>
                  {groupedBundleColours &&
                    Object.entries(groupedBundleColours).map(([family, list]) => (
                      <optgroup key={family} label={family}>
                        {list.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.code} — {c.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                </select>

                {selectedBundleColour && (
                  <div
                    data-testid="colour-swatch-preview"
                    className="mt-4 flex items-center gap-4 rounded-2xl border border-[hsl(var(--onyx)/0.08)] bg-blush/40 p-4"
                  >
                    <div className="w-14 h-14 rounded-full border border-[hsl(var(--onyx)/0.12)] shadow-inner flex items-center justify-center font-display text-sm text-[hsl(var(--burgundy))] bg-white">
                      {selectedBundleColour.code}
                    </div>
                    <div className="flex-1">
                      <div className="font-display text-lg leading-tight">
                        {selectedBundleColour.label}
                      </div>
                      <div className="text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.55)] mt-1">
                        {selectedBundleColour.family}
                      </div>
                    </div>
                    {p.colour_chart && (
                      <button
                        type="button"
                        onClick={() => setChartOpen(true)}
                        className="w-16 h-16 rounded-xl overflow-hidden border border-[hsl(var(--onyx)/0.12)] shrink-0"
                        aria-label="Open colour chart"
                      >
                        <img
                          src={p.colour_chart}
                          alt="Colour chart"
                          className="w-full h-full object-cover"
                        />
                      </button>
                    )}
                  </div>
                )}

                <p className="mt-3 text-xs text-[hsl(var(--onyx)/0.55)] leading-relaxed">
                  Hair colours may appear slightly different on screen depending
                  on lighting, monitor calibration and personal perception. For
                  the closest match, please refer to the full colour chart.
                </p>
                <Link
                  to="/colour-match"
                  data-testid="colour-help-link"
                  className="mt-2 inline-flex items-center gap-2 text-sm text-[hsl(var(--burgundy))] hover:underline"
                >
                  <HelpCircle size={14} /> Need help with colour matching?
                </Link>
              </div>
            ) : p.colours?.length ? (
              <div className="mt-8">
                <div className="overline mb-3">Colour</div>
                <select
                  value={colour}
                  onChange={(e) => setColour(e.target.value)}
                  data-testid="colour-select"
                  className="w-full h-12 rounded-2xl border border-[hsl(var(--onyx)/0.15)] bg-white px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                >
                  <option value="">-- Choose a colour --</option>
                  {p.colours.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
            ) : null}

            <div className="mt-8">
              <div className="overline mb-3">Quantity</div>
              <div className="inline-flex items-center gap-4 rounded-full border border-[hsl(var(--onyx)/0.15)] px-4 py-2">
                <button
                  data-testid="qty-decrement"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="w-8 h-8 rounded-full hover:bg-[hsl(var(--onyx)/0.06)] flex items-center justify-center"
                >
                  <Minus size={14} />
                </button>
                <span className="min-w-[24px] text-center font-medium">{qty}</span>
                <button
                  data-testid="qty-increment"
                  onClick={() => setQty((q) => {
                    const cap = typeof p.stock === "number" ? Math.min(20, p.stock) : 20;
                    return Math.min(cap, q + 1);
                  })}
                  className="w-8 h-8 rounded-full hover:bg-[hsl(var(--onyx)/0.06)] flex items-center justify-center"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>

            {typeof p.stock === "number" && p.stock === 0 && (
              <div
                data-testid="product-sold-out"
                className="mt-6 rounded-2xl bg-[hsl(var(--onyx))] text-[hsl(var(--cream))] px-5 py-3 text-sm inline-flex items-center gap-2"
              >
                <span className="uppercase tracking-widest text-[10px]">Sold Out</span>
                <span className="text-[hsl(var(--cream)/0.7)]">— check back soon or join the waitlist below.</span>
              </div>
            )}
            {typeof p.stock === "number" && p.stock > 0 && p.stock <= 3 && (
              <div
                data-testid="product-low-stock"
                className="mt-6 rounded-2xl bg-[hsl(var(--burgundy))] text-[hsl(var(--cream))] px-5 py-2.5 text-sm inline-flex items-center gap-2"
              >
                <span className="uppercase tracking-widest text-[10px]">Only {p.stock} left</span>
                <span className="text-[hsl(var(--cream)/0.75)]">— once they're gone, they're gone.</span>
              </div>
            )}

            <div className="mt-10 flex flex-wrap gap-3">
              <button
                onClick={addToCart}
                disabled={typeof p.stock === "number" && p.stock === 0}
                data-testid="product-add-to-cart"
                className="btn-pill btn-primary flex-1 min-w-[220px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {typeof p.stock === "number" && p.stock === 0
                  ? "Sold Out"
                  : (<>Add to cart · £{(effectivePrice() * qty).toFixed(2)} <ArrowUpRight size={16} /></>)}
              </button>
              <button
                onClick={toggleWish}
                data-testid="product-wishlist"
                aria-label="Toggle wishlist"
                className={`btn-pill ${isWish ? "btn-primary" : "btn-outline"}`}
              >
                <Heart size={16} fill={isWish ? "currentColor" : "none"} />
              </button>
            </div>

            {p.digital && (
              <p
                data-testid="digital-notice"
                className="mt-3 text-xs text-[hsl(var(--onyx)/0.6)] leading-relaxed"
              >
                Digital product — no physical item will be posted. Your files
                will be available immediately after successful payment and will
                also be sent to the email address used at checkout.
              </p>
            )}

            <div className="mt-10 pt-8 border-t border-[hsl(var(--onyx)/0.08)] space-y-3 text-sm text-[hsl(var(--onyx)/0.65)]">
              <div className="flex items-center gap-3">
                <Truck size={16} className="text-[hsl(var(--gold))]" />
                {p.digital
                  ? "Instant access in your account library"
                  : "Free UK shipping over £50 · flat £4.95 otherwise"}
              </div>
              <div className="text-xs text-[hsl(var(--onyx)/0.5)]">
                Secure checkout · Stripe · Klarna & Clearpay available
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Colour chart modal */}
      {chartOpen && p.colour_chart && (
        <div
          role="dialog"
          data-testid="colour-chart-modal"
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setChartOpen(false)}
        >
          <div
            className="relative max-w-[900px] w-full bg-white rounded-[2rem] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setChartOpen(false)}
              aria-label="Close"
              data-testid="close-colour-chart"
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow"
            >
              <X size={18} />
            </button>
            <img
              src={p.colour_chart}
              alt="Hair colour chart"
              className="w-full h-auto object-contain"
            />
            <div className="px-6 py-4 text-xs text-[hsl(var(--onyx)/0.6)] border-t border-[hsl(var(--onyx)/0.06)]">
              Colours may appear slightly different on screen. Not sure which
              tone suits you?{" "}
              <Link
                to="/colour-match"
                className="text-[hsl(var(--burgundy))] hover:underline"
              >
                Send us your photo for a personal match.
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
